import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { useAnalysisRouteParams } from './useAnalysisRouteParams';
import { useAnalysisSourceCohort } from './useAnalysisSourceCohort';
import { analysisKeys } from '../api/queryKeys';

/**
 * Re-reads the analysis's readiness and results when its source cohort's status or
 * version changes, however the change reached the page (a cohort run finishing, a
 * save). Readiness lists "cohort not processed" as a Process blocker and the
 * results carry the stale flag, and both are computed from the cohort's state on
 * the server, so a change here would otherwise leave the Process button and the
 * stale banner describing the cohort as it was when the page loaded.
 *
 * The first value the cohort list delivers is the baseline, not a change.
 */
export const useRefreshOnSourceCohortChange = (): void => {
  const queryClient = useQueryClient();
  const { analysisProfileId } = useAnalysisRouteParams();
  const { selectedCohort } = useAnalysisSourceCohort();

  const signature = selectedCohort
    ? `${selectedCohort.latestStatus}:${selectedCohort.latestCohortVersionId ?? ''}`
    : null;
  const previousSignature = useRef<string | null>(null);

  useEffect(() => {
    if (
      previousSignature.current !== null &&
      signature !== null &&
      signature !== previousSignature.current
    ) {
      void queryClient.invalidateQueries({ queryKey: analysisKeys.readinessAll() });
      void queryClient.invalidateQueries({ queryKey: analysisKeys.results(analysisProfileId) });
    }
    if (signature !== null) previousSignature.current = signature;
  }, [signature, queryClient, analysisProfileId]);
};
